import { useState, useMemo } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { useDemoState } from "@/data/demoState";
import PackPreviewModal from "@/components/shared/PackPreviewModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Gift, Home, Key, FileText, Image, CheckCircle2, AlertTriangle,
  MessageSquare, Sparkles, Send, Shield, ArrowRight, Star,
  DoorOpen, Droplets, Zap, Flame, Sun, Wind, ChevronRight, User, Eye, Download
} from "lucide-react";

const rooms = [
  { id: 'living', name: 'Living & Dining', icon: Home, status: 'complete', items: ['Painting complete', 'Flooring installed', 'Downlights fitted'], defects: 0, photos: 4 },
  { id: 'kitchen', name: 'Kitchen', icon: Flame, status: 'complete', items: ['Benchtop installed', 'Splashback tiled', 'Appliances connected'], defects: 0, photos: 6 },
  { id: 'master', name: 'Master Bedroom', icon: DoorOpen, status: 'complete', items: ['Walk-in robe fitted', 'Ensuite plumbed', 'Carpet laid'], defects: 0, photos: 3 },
  { id: 'bath1', name: 'Bathroom 1', icon: Droplets, status: 'minor-defect', items: ['Tiles grouted', 'Vanity installed', 'Shower screen fitted'], defects: 1, photos: 5, defectDetail: 'Minor grout discoloration near shower base. Touch-up scheduled.' },
  { id: 'bath2', name: 'Bathroom 2', icon: Droplets, status: 'complete', items: ['All fixtures installed', 'Waterproofing cert'], defects: 0, photos: 3 },
  { id: 'bed2', name: 'Bedroom 2', icon: DoorOpen, status: 'complete', items: ['Painting complete', 'Carpet laid'], defects: 0, photos: 2 },
  { id: 'bed3', name: 'Bedroom 3', icon: DoorOpen, status: 'complete', items: ['Painting complete', 'Carpet laid'], defects: 0, photos: 2 },
  { id: 'garage', name: 'Garage', icon: Home, status: 'minor-defect', items: ['Auto door fitted', 'Epoxy floor', 'Storage shelving'], defects: 1, photos: 3, defectDetail: 'Small chip in epoxy near entry step. Repair included in defect period.' },
  { id: 'outdoor', name: 'Outdoor / Landscaping', icon: Sun, status: 'complete', items: ['Decking installed', 'Turf laid', 'Fence complete'], defects: 0, photos: 5 },
  { id: 'services', name: 'Services & Utilities', icon: Zap, status: 'complete', items: ['Meter board connected', 'HWS commissioned', 'Solar installed'], defects: 0, photos: 2 },
];

const conciergeResponses = [
  { prompt: "When is my settlement date?", response: "Based on the current project timeline, your settlement date is expected around 28 February 2025. The OC was issued on 15 January, and your conveyancer has been notified. You'll receive a formal notice 14 days prior." },
  { prompt: "What warranty covers my home?", response: "Your home is covered by:\n- 6 year structural warranty (VBA registered)\n- 2 year non-structural defect period\n- Individual manufacturer warranties for appliances\nAll warranty certificates are in your handover pack." },
  { prompt: "I noticed a crack in the bathroom tile", response: "Thanks for reporting this. I've logged it as a defect item. Here's what happens next:\n1. A site supervisor will inspect within 5 business days\n2. If confirmed, it'll be scheduled for repair\n3. You'll receive a confirmation once repaired\nThis is covered under your 2-year defect period." },
  { prompt: "How do I use the ducted heating?", response: "Your home has a Brivis StarPro SP623 ducted gas system:\n- Thermostat is in the hallway\n- Set desired temperature and mode (heat/cool)\n- Zones: Living (zone 1), Bedrooms (zone 2)\n- Filter cleaning: every 3 months\nThe user manual is included in your digital handover pack." },
];

export default function BuyerHandover() {
  const { projects, evidence, buyers, workspace } = useDemoState();
  const [tab, setTab] = useState('overview');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [showConcierge, setShowConcierge] = useState(false);
  const [showPackPreview, setShowPackPreview] = useState(false);

  const buyerProjects = projects.filter(p => p.visibility?.buyerHandover);
  const activeBuyerProject = buyerProjects[0];
  const buyer = buyers.find(b => b.projectId === activeBuyerProject?.id);

  const totalDefects = rooms.reduce((acc, r) => acc + r.defects, 0);
  const completeRooms = rooms.filter(r => r.status === 'complete').length;

  const handleConcierge = (prompt) => {
    const response = conciergeResponses.find(r => r.prompt === prompt);
    setChatHistory(prev => [...prev, { role: 'user', text: prompt }, { role: 'ai', text: response?.response || "I'll look into that and get back to you shortly. Your builder PM has been notified." }]);
    setShowConcierge(true);
  };

  const handleCustomQuestion = () => {
    if (!chatInput.trim()) return;
    setChatHistory(prev => [...prev, { role: 'user', text: chatInput }, { role: 'ai', text: "Thank you for your question. I've forwarded this to the project team and you'll receive a response within 24 hours. In the meantime, you can check your handover pack for related documentation." }]);
    setChatInput('');
  };

  if (!activeBuyerProject) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-20 space-y-4" data-testid="buyer-handover-empty">
          <Gift className="w-12 h-12 text-slate-300" />
          <p className="text-sm text-slate-500">No projects with buyer handover enabled in this workspace</p>
          <p className="text-xs text-slate-400">Switch to UrbanNest Developments to see the buyer handover experience</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="buyer-handover-page">
        {/* Hero */}
        <Card className="border-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden" data-testid="handover-hero">
          <CardContent className="py-6 px-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Key className="w-5 h-5 text-amber-400" />
                  <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Buyer Handover</span>
                </div>
                <h2 className="text-xl font-heading font-bold mb-1">{activeBuyerProject.name}</h2>
                <p className="text-sm text-slate-300">{activeBuyerProject.address}</p>
                {buyer && <p className="text-xs text-slate-400 mt-1">Prepared for {buyer.name}</p>}
              </div>
              <div className="text-right space-y-1">
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                  {activeBuyerProject.completionPercent}% complete
                </Badge>
                <p className="text-xs text-slate-400">{completeRooms}/{rooms.length} rooms complete</p>
                <p className="text-xs text-slate-400">{totalDefects} defect(s) noted</p>
                <div className="flex gap-1.5 mt-1.5">
                  <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white h-7 text-[10px] gap-1" onClick={() => setShowPackPreview(true)} data-testid="buyer-preview-pack-btn"><Eye className="w-3 h-3" />Preview pack</Button>
                  <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white h-7 text-[10px] gap-1" onClick={() => toast.success('PDF exported')}><Download className="w-3 h-3" />Export</Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100 h-9" data-testid="handover-tabs">
            <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
            <TabsTrigger value="rooms" className="text-xs">Room Explorer</TabsTrigger>
            <TabsTrigger value="documents" className="text-xs">Documents</TabsTrigger>
            <TabsTrigger value="concierge" className="text-xs flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Concierge</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-4 space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: "Rooms complete", value: `${completeRooms}/${rooms.length}`, icon: Home, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-100" },
                { label: "Defects", value: totalDefects, icon: AlertTriangle, color: totalDefects > 0 ? "text-amber-600" : "text-emerald-600", bg: totalDefects > 0 ? "bg-amber-50" : "bg-emerald-50", border: totalDefects > 0 ? "border-amber-100" : "border-emerald-100" },
                { label: "Documents", value: "8 ready", icon: FileText, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-100" },
                { label: "Photos", value: rooms.reduce((a, r) => a + r.photos, 0), icon: Image, color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-100" },
              ].map(s => (
                <Card key={s.label} className={`${s.bg} ${s.border} border`}>
                  <CardContent className="py-3 px-4 flex items-center gap-3">
                    <s.icon className={`w-5 h-5 ${s.color}`} />
                    <div><p className={`text-xl font-heading font-bold ${s.color}`}>{s.value}</p><p className="text-[10px] text-slate-500">{s.label}</p></div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Quick concierge prompts */}
            <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50/50 to-white" data-testid="quick-concierge">
              <CardContent className="py-4 px-5">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-indigo-600" />
                  <span className="text-xs font-bold text-indigo-800">AI Handover Concierge</span>
                  <span className="text-[10px] text-indigo-500">Ask anything about your new home</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {conciergeResponses.map((cr, i) => (
                    <button key={i} onClick={() => { handleConcierge(cr.prompt); setTab('concierge'); }} className="text-left p-2.5 rounded-lg border border-indigo-100 bg-white hover:bg-indigo-50 transition-colors">
                      <p className="text-xs text-indigo-700 font-medium">{cr.prompt}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Room summary grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="room-summary-grid">
              {rooms.map(r => {
                const Icon = r.icon;
                return (
                  <button key={r.id} onClick={() => { setSelectedRoom(r); setTab('rooms'); }} className={`p-3 rounded-xl border-2 text-center hover:shadow-md transition-all ${r.status === 'complete' ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
                    <Icon className={`w-5 h-5 mx-auto mb-1.5 ${r.status === 'complete' ? 'text-emerald-600' : 'text-amber-600'}`} />
                    <p className="text-[10px] font-semibold text-slate-700">{r.name}</p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      {r.status === 'complete' ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <AlertTriangle className="w-3 h-3 text-amber-500" />}
                      <span className={`text-[9px] ${r.status === 'complete' ? 'text-emerald-600' : 'text-amber-600'}`}>{r.defects > 0 ? `${r.defects} defect` : 'Complete'}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </TabsContent>

          {/* Room Explorer */}
          <TabsContent value="rooms" className="mt-4 space-y-3">
            {rooms.map(r => {
              const Icon = r.icon;
              const isExpanded = selectedRoom?.id === r.id;
              return (
                <Card key={r.id} className={`border-slate-200 hover:shadow-sm transition-all cursor-pointer ${isExpanded ? 'ring-2 ring-indigo-200' : ''}`} onClick={() => setSelectedRoom(isExpanded ? null : r)} data-testid={`room-${r.id}`}>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${r.status === 'complete' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                          <Icon className={`w-5 h-5 ${r.status === 'complete' ? 'text-emerald-600' : 'text-amber-600'}`} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{r.name}</p>
                          <p className="text-[10px] text-slate-500">{r.items.length} items &middot; {r.photos} photos</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.defects > 0 && <Badge className="text-[9px] bg-amber-100 text-amber-700">{r.defects} defect</Badge>}
                        <Badge className={`text-[9px] ${r.status === 'complete' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {r.status === 'complete' ? 'Complete' : 'Minor defect'}
                        </Badge>
                        <ChevronRight className={`w-4 h-4 text-slate-300 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                        <div className="space-y-1">
                          {r.items.map((item, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-slate-600"><CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />{item}</div>
                          ))}
                        </div>
                        {r.defectDetail && (
                          <div className="p-2.5 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700">
                            <AlertTriangle className="w-3 h-3 text-amber-500 inline mr-1" />{r.defectDetail}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* Documents */}
          <TabsContent value="documents" className="mt-4 space-y-3">
            {[
              { name: 'Certificate of Occupancy', type: 'Certificate', date: '15/01/2025', status: 'ready' },
              { name: 'Domestic Building Insurance', type: 'Insurance', date: '01/11/2024', status: 'ready' },
              { name: 'Structural Warranty Certificate', type: 'Warranty', date: '15/01/2025', status: 'ready' },
              { name: 'Waterproofing Certificate', type: 'Certificate', date: '10/12/2024', status: 'ready' },
              { name: 'Electrical Safety Certificate', type: 'Certificate', date: '08/01/2025', status: 'ready' },
              { name: 'Plumbing Compliance Certificate', type: 'Certificate', date: '05/01/2025', status: 'ready' },
              { name: 'Energy Rating Certificate', type: 'Certificate', date: '15/11/2024', status: 'ready' },
              { name: 'Appliance User Manuals Bundle', type: 'Manual', date: '15/01/2025', status: 'ready' },
            ].map((doc, i) => (
              <Card key={i} className="border-slate-200 hover:shadow-sm transition-all" data-testid={`doc-${i}`}>
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{doc.name}</p>
                    <p className="text-[10px] text-slate-500">{doc.type} &middot; {doc.date}</p>
                  </div>
                  <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Ready</Badge>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* AI Concierge */}
          <TabsContent value="concierge" className="mt-4" data-testid="ai-concierge-tab">
            <Card className="border-indigo-200 bg-gradient-to-b from-indigo-50/30 to-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2"><Sparkles className="w-5 h-5 text-indigo-600" /> AI Handover Concierge</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Chat history */}
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {chatHistory.length === 0 && (
                    <div className="text-center py-8">
                      <Sparkles className="w-8 h-8 text-indigo-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">Ask anything about your new home</p>
                      <p className="text-xs text-slate-400 mt-1">Warranty, settlement, defects, how things work...</p>
                    </div>
                  )}
                  {chatHistory.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] p-3 rounded-xl text-xs leading-relaxed ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-br-none' : 'bg-white border border-indigo-100 text-slate-700 rounded-bl-none'}`}>
                        {msg.role === 'ai' && <div className="flex items-center gap-1.5 mb-1.5 text-indigo-600 font-semibold"><Sparkles className="w-3 h-3" /> Concierge</div>}
                        <p className="whitespace-pre-wrap">{msg.text}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quick prompts */}
                <div className="flex flex-wrap gap-2">
                  {conciergeResponses.filter(cr => !chatHistory.some(ch => ch.text === cr.prompt)).slice(0, 3).map((cr, i) => (
                    <button key={i} onClick={() => handleConcierge(cr.prompt)} className="text-xs px-3 py-1.5 rounded-full border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors">
                      {cr.prompt}
                    </button>
                  ))}
                </div>

                {/* Custom input */}
                <div className="flex gap-2">
                  <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCustomQuestion()} placeholder="Ask about your new home..." className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-200" data-testid="concierge-input" />
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleCustomQuestion} data-testid="concierge-send"><Send className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <PackPreviewModal open={showPackPreview} onClose={() => setShowPackPreview(false)} type="buyer" data={{ projectName: activeBuyerProject?.name, address: activeBuyerProject?.address, workspace: workspace.name }} />
    </AppLayout>
  );
}
